import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import useAsyncEffect from 'use-async-effect';
import { InspectorWidgetLayout } from '@/features/editor/widgets/shared/inspector-widget-layout';
import { EditorContext } from '@/features/editor/context/editor-context';
import { TextInput } from '@/components/text-input';
import { DeviceType } from '@/utils/definitions';
import { BigButton } from '@/components/big-button';
import { VSpace } from '@/components/spacing';
import {
  ExperienceInfoResponse,
  UNNAMED_EXPERIENCE,
  useExperienceApi,
} from '@/webapi/use-experience-api';
import { RichTextDescription } from '@/features/shared/experience-info-modal/rich-text-description';
import { Label, LabelsList } from '@/components/labels-list';
import { ImageThumbnailGallery } from '@/features/shared/experience-info-modal/image-thumbnail-gallery';
import { newMyExperiencesContext } from '@/features/dashboard/experiences/context';
import { CONTROL } from '@/features/details/shared';

export enum ExperienceNameOrigin {
  SAVE = 0,
  PREVIEW = 1,
  PUBLISH = 2,
  TARGETING = 3,
}

export function ExperienceNameWidget({
  origin,
}: {
  origin: ExperienceNameOrigin;
}) {
  const { experienceState, devicePreview, inspectorNav, experienceControls } =
    useContext(EditorContext);
  const {
    editorState: { device },
  } = devicePreview;
  const textRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(experienceState.currentExperience.name);
  const [description, setDescription] = useState(
    experienceState.currentExperience.description,
  );
  const { isExpDetailsEnhancement } = newMyExperiencesContext();
  const experienceId = experienceState.currentExperience.id;

  const filteredVariants = useMemo(
    () =>
      experienceState?.currentExperience?.variants?.filter(
        (variant) => variant.name !== CONTROL,
      ) || [],
    [experienceState?.currentExperience?.variants],
  );

  const { getExperienceInfo, removeLabels } = useExperienceApi();

  const [data, setData] = useState<ExperienceInfoResponse | null>(null);

  const transformedLabels = useMemo<Label[]>(
    () =>
      data?.labels?.map((l) => ({
        label: l?.label,
        isSelected: l?.selected === true,
      })) || [],
    [data],
  );

  useAsyncEffect(async () => {
    const info = await getExperienceInfo(experienceId);
    setData(info);
  }, [experienceId]);

  const onBeforeImagesChanged = (images: string[]) => {
    setData((draft) => ({ ...draft, beforeImages: images }));
  };
  const onVariantImagesChanged = (variantId: string, images: string[]) => {
    setData((draft) => ({
      ...draft,
      variationImages: {
        ...draft.variationImages,
        [variantId]: { values: images },
      },
    }));
  };

  const onLabelAdded = async (label: string) => {
    setData((draft) => ({
      ...draft,
      labels: [...(draft.labels || []), { label, selected: true }],
    }));
  };

  const onLabelRemoved = async (label: string) => {
    await removeLabels([label]);
    setData((draft) => ({
      ...draft,
      labels: draft.labels?.filter((l) => l.label !== label),
    }));
  };

  const onLabelSelected = async (selected: string[]) => {
    const updated = data?.labels?.map((l) => {
      if (selected.includes(l.label)) {
        return { ...l, selected: true };
      }
      return { ...l, selected: false };
    });
    setData((draft) => ({
      ...draft,
      labels: updated,
    }));
  };

  const onNext = async () => {
    if (isExpDetailsEnhancement && name) {
      const updatedData = {
        ...data,
        name,
        description,
      };
      setData(updatedData);
      experienceState.setExperienceInfo(updatedData);
    } else {
      experienceState.setExperienceName(name, description);
    }
    if (origin === ExperienceNameOrigin.SAVE) {
      await experienceControls.forceSave(name, description);
      inspectorNav.gotoChangelog();
    } else if (origin === ExperienceNameOrigin.PREVIEW) {
      await experienceControls.preview(undefined, undefined);
      inspectorNav.gotoChangelog();
    } else if (origin === ExperienceNameOrigin.PUBLISH) {
      await experienceControls.publish();
      experienceControls.closeAndNavigateToDashboard();
    } else if (origin === ExperienceNameOrigin.TARGETING) {
      inspectorNav.gotoTargeting();
    }
  };

  const onNameChange = (ev) => {
    setName(ev.target.value);
  };

  const isNameValid = useCallback(
    () => name !== UNNAMED_EXPERIENCE && name !== ``,
    [name],
  );

  useEffect(() => {
    if (textRef) {
      textRef?.current?.focus();
      setTimeout(() => {
        textRef?.current?.focus();
      }, 2000);
    }
  }, [textRef]);

  return (
    <InspectorWidgetLayout
      title="Name your experience"
      progress={0}
      backCaption="< Back to changes"
      onBackClicked={inspectorNav.gotoChangelog}
    >
      <VSpace value={3} />
      <LabelText>Experience Name</LabelText>
      <VSpace value={1} />
      <NameInput
        ref={textRef}
        device={device}
        placeholder={name}
        defaultValue={isNameValid() ? name : undefined}
        onChange={onNameChange}
      />
      <VSpace value={3} />
      <LabelText>Experience description (optional)</LabelText>
      <VSpace value={1} />
      <Frame device={device}>
        <RichTextDescription
          defaultValue={description}
          onChange={setDescription}
        />
      </Frame>
      <VSpace value={3} />
      {isExpDetailsEnhancement && (
        <>
          <LabelText>Add labels</LabelText>
          <VSpace value={1} />

          <LabelsList
            maxWidth={device === DeviceType.Desktop ? `100%` : `60%`}
            labels={transformedLabels}
            onRemoveLabel={onLabelRemoved}
            onLabelSelection={onLabelSelected}
            onAddLabel={onLabelAdded}
            allowAppend
            collapseAfter={7}
          />
          {filteredVariants.length > 0 && (
            <Row>
              <LabelText>Add images</LabelText>
              <Frame>
                <SmallCaption>Control</SmallCaption>
                <ImageThumbnailGallery
                  onImageListChanged={onBeforeImagesChanged}
                  maxImages={5}
                  allowUpload
                  initialValue={data?.beforeImages || []}
                />
              </Frame>
              {filteredVariants.map((variant) => (
                <Frame key={variant.id}>
                  <SmallCaption>{variant.name}</SmallCaption>
                  <ImageThumbnailGallery
                    onImageListChanged={(images) =>
                      onVariantImagesChanged(variant.id, images)
                    }
                    maxImages={5}
                    allowUpload
                    initialValue={
                      data?.variationImages?.[variant.id]?.values || []
                    }
                    isExpDetailsEnhancement
                  />
                </Frame>
              ))}
              <VSpace value={3} />
            </Row>
          )}
        </>
      )}
      <BigButton
        disabled={!isNameValid()}
        border="2px solid black"
        noTransform
        size={device === DeviceType.Desktop ? `medium` : `large`}
        fillWidth={device === DeviceType.Desktop}
        onClick={onNext}
      >
        Next
      </BigButton>
    </InspectorWidgetLayout>
  );
}

const LabelText = styled.span`
  color: #b9c0c7;
  font-size: 1.4rem;
  font-family: Inter, serif;
  font-weight: 500;
`;

const Frame = styled.div`
  border: 1px solid rgb(221, 219, 219);
  border-radius: 1.5rem;
  padding: 1.3rem;
  max-width: ${(props: { device: DeviceType }) =>
    props.device === DeviceType.Desktop ? `100%` : `60%`};
`;

const NameInput = styled(TextInput)`
  && {
    border: 1px solid rgb(221, 219, 219);
    box-shadow: none;
    margin: 0;
    width: ${(props: { device: DeviceType }) =>
      props.device === DeviceType.Desktop ? `100%` : `60%`};
    padding: ${(props: { device: DeviceType }) =>
      props.device === DeviceType.Desktop ? `1.2rem` : `1.8rem`};
    font-size: ${(props: { device: DeviceType }) =>
      props.device === DeviceType.Desktop ? `1.4rem` : `1.6rem`};
    font-weight: 500;
  }
`;
const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;
const SmallCaption = styled.p`
  margin: 0;
  padding: 0;
  color: black;
  opacity: 0.38;
  font-size: 1.2rem;
  font-family: Inter, serif;
  font-weight: 300;
  text-align: start;
`;
